import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || '');
  const [sortBy, setSortBy] = useState<'rating' | 'popularity' | 'latest'>('rating');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const filteredComics = comics.filter((comic) => {
    if (!activeCategory) return true;
    return comic.category === activeCategory || comic.tags.includes(activeCategory);
  });

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'popularity') return b.popularity - a.popularity;
    return b.id - a.id;
  });

  const mainCategories = categories.filter((c) => ['hanman', 'riman', 'guoman'].includes(c.slug));
  const subCategories = categories.filter((c) => !['hanman', 'riman', 'guoman'].includes(c.slug));

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory ? `${currentCategory.name} - 新新漫画` : '漫画分类 - 新新漫画',
    description: currentCategory?.description || '新新漫画类型分类，韩漫日漫国漫大全',
    url: `https://xinxinmanhua.com/category/${activeCategory}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: sortedComics.slice(0, 20).map((comic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'ComicSeries',
          name: comic.title,
          url: `https://xinxinmanhua.com/comic/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: currentCategory?.name || '分类' },
                ]}
              />
            </div>

            {/* Page Header */}
            <div className="mb-10">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                {currentCategory ? `${currentCategory.name}` : '漫画分类'}
              </h1>
              {currentCategory && (
                <p className="text-foreground-500 mt-2 text-sm md:text-base">
                  {currentCategory.description} · 共 {filteredComics.length} 部作品
                </p>
              )}
            </div>

            {/* Category Tabs */}
            <div className="mb-8">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500 mr-1 whitespace-nowrap">地区：</span>
                {mainCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <span className="text-sm text-foreground-500 mr-1 whitespace-nowrap">类型：</span>
                <Link
                  to="/category/hanman"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    !activeCategory || ['hanman', 'riman', 'guoman'].includes(activeCategory)
                      ? 'bg-primary-500 text-white'
                      : 'bg-white text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200'
                  }`}
                >
                  全部
                </Link>
                {subCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Sort Bar */}
            <div className="flex items-center justify-between mb-8 bg-white rounded-lg px-4 py-3 border border-background-200">
              <span className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-800">{sortedComics.length}</strong> 部漫画
              </span>
              <div className="flex items-center gap-2">
                {([
                  { key: 'rating' as const, label: '评分最高' },
                  { key: 'popularity' as const, label: '人气最热' },
                  { key: 'latest' as const, label: '最新收录' },
                ]).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-500 hover:bg-background-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Comic Grid */}
            {sortedComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 md:gap-6" data-product-shop>
                {sortedComics.map((comic) => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto bg-background-200 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400" />
                </div>
                <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
                <Link
                  to="/"
                  className="inline-block mt-4 px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  回到首页
                </Link>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}